// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { ReactNode } from 'react';
import type { BankAccount } from '../../types/openBanking';
import './BankAccountCard.css';

interface BankAccountCardProps {
  account: BankAccount;
  /** Optional secondary content: chart, transactions table, etc. */
  children?: ReactNode;
  /** Highlight when the user has selected this account. */
  selected?: boolean;
  onClick?: () => void;
}

/**
 * Displays one linked bank account: bank logo + name, account name +
 * masked IBAN, balance + currency, and a status badge for the loading
 * lifecycle. Optional children render below for the drill-down view
 * (transactions table, chart, etc.).
 */
export function BankAccountCard({ account, children, selected, onClick }: BankAccountCardProps) {
  const masked = maskIban(account.iban);
  const isClickable = Boolean(onClick);
  return (
    <div
      className={`bank-account-card${selected ? ' bank-account-card--selected' : ''}${isClickable ? ' bank-account-card--clickable' : ''}`}
      onClick={onClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      } : undefined}
    >
      <div className="bank-account-card__header">
        <div className="bank-account-card__bank">
          {account.bankLogoPng ? (
            <img
              src={account.bankLogoPng}
              alt={`${account.bankName} logo`}
              className="bank-account-card__logo"
            />
          ) : null}
          <div>
            <div className="bank-account-card__bank-name">{account.bankName}</div>
            <div className="bank-account-card__account-name">{account.name}</div>
          </div>
        </div>
        <div className="bank-account-card__balance">
          <div className="bank-account-card__balance-amount">
            {formatCurrency(account.balanceAmount, account.currencyCode)}
          </div>
          <div className="bank-account-card__balance-label">Available balance</div>
        </div>
      </div>
      <div className="bank-account-card__meta">
        <span className="bank-account-card__iban" title={account.iban}>{masked}</span>
        <span className="bank-account-card__type">{labelForType(account.type)}</span>
        {account.status ? <StatusBadge status={account.status} /> : null}
      </div>
      {children ? <div className="bank-account-card__body">{children}</div> : null}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone = badgeTone(status);
  return (
    <span className={`bank-account-card__status bank-account-card__status--${tone}`}>
      {humaniseStatus(status)}
    </span>
  );
}

function badgeTone(status: string): 'success' | 'warning' | 'error' | 'neutral' {
  if (status === 'LoadingCompleted') return 'success';
  if (status.endsWith('Failed')) return 'error';
  if (status.startsWith('Loading') || status === 'Refreshing') return 'warning';
  return 'neutral';
}

function humaniseStatus(status: string): string {
  // "LoadingTransactions" → "Loading transactions".
  return status.replace(/([A-Z])/g, ' $1').trim().replace(/^./, (c) => c.toUpperCase());
}

function labelForType(type: string): string {
  if (type === 'CHECKING') return 'Personal';
  if (type === 'SAVINGS') return 'Savings';
  return type.charAt(0) + type.slice(1).toLowerCase();
}

function maskIban(iban?: string): string {
  if (!iban) return '';
  if (iban.length <= 8) return iban;
  return `${iban.slice(0, 4)} •••• ${iban.slice(-4)}`;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
